import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { fetchPowermeterHistoryDataBasedOnView, HistoryPayloadBasedOnView } from '../api'
import { PowermeterHistoryChartData, PowermeterInfoArray } from '../models'
import { fromPowermeterHistoryToRechartData } from '../transformers/from-powermeter-history-to-rechart-data.transformer'
import { usePortalQueries } from 'src/Libs/api'

interface ReturnType {
  powermeterData: PowermeterHistoryChartData[]
  isLoading: boolean
}

/**
 * This hook fetches powermeter data. It uses a powermeter step route which is an alternative to the default powermeter route.
 * The route has the potential to be more performant.
 *
 * TODO: Currently no access to "single" powermeter data, instead all data is merged
 * TODO: Currently only considereing Powermeter_Production and Powermeter_Consumption. Not considered: bypass, bypass_noCharge, NetIn_enera
 *
 * @param storageId
 * @param powermeters - an installation can have multiple powermeters and all of them are relevant for fetching data
 * @param payload
 * @returns
 */
export const useFetchPowermeterHistoryViewData = (powermeters: PowermeterInfoArray[], payload: HistoryPayloadBasedOnView): ReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const { view } = payload

  const powermeterQueries = powermeters.map(powermeter => ({
    queryKey: ['powermeter-data', powermeter.storageId, powermeter.id, payload],
    queryFn: () => {
      const powermeterStorageId = powermeter.storageId?.toString()
      return fetchPowermeterHistoryDataBasedOnView(powermeterStorageId, powermeter.id.toString(), authFetch, payload)
    },
    enabled: powermeter.storageId !== undefined,
    refetchOnWindowFocus: true,
  }))

  const powermeterDataResults = usePortalQueries(powermeterQueries)
  const isLoading = powermeterDataResults.some(data => data.isLoading)

  const factor = view === 'day' ? 4 : 1
  let powermeterDataMap = new Map<number, PowermeterHistoryChartData>()

  Object.entries(powermeterDataResults).forEach(([key, result]) => {
    if (result.data) {
      const powermeter = powermeters[Number(key)]
      powermeterDataMap = fromPowermeterHistoryToRechartData(powermeter, result.data, factor, powermeterDataMap)
    }
  })

  const powermeterData: PowermeterHistoryChartData[] = Array.from(powermeterDataMap.values())

  return { powermeterData, isLoading }
}

interface ReturnTypeFetchManual {
  handleFetchingPowermeterHistoryData: (
    powermeters: PowermeterInfoArray[],
    payload: HistoryPayloadBasedOnView
  ) => Promise<PowermeterHistoryChartData[]>
  isFetching: boolean
}

/**
 * Alternative hook to useFetchPowermeterHistoryViewData. Instead of fetching data automatically it allows to fetch data manually by exposing a fetch function.
 * @param powermeters
 * @param payload
 * @returns
 */
export const useFetchPowermeterHistoryViewDataManual = (): ReturnTypeFetchManual => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  const isFetching = useIsFetching({ queryKey: ['powermeter-data-manual'] }) > 0

  const handleFetchingPowermeterHistoryData = async (
    powermeters: PowermeterInfoArray[],
    payload: HistoryPayloadBasedOnView
  ): Promise<PowermeterHistoryChartData[]> => {
    const factor = payload.view === 'day' ? 4 : 1
    let powermeterDataMap = new Map<number, PowermeterHistoryChartData>()

    const results = await Promise.allSettled(
      powermeters.map(powermeter => {
        const powermeterStorageId = powermeter.storageId?.toString()
        return queryClient.fetchQuery({
          queryKey: ['powermeter-data-manual', powermeter.storageId, powermeter.id, payload],
          queryFn: () => fetchPowermeterHistoryDataBasedOnView(powermeterStorageId, powermeter.id.toString(), authFetch, payload),
        })
      })
    )

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        const powermeter = powermeters[index]
        powermeterDataMap = fromPowermeterHistoryToRechartData(powermeter, result.value, factor, powermeterDataMap)
      }
    })

    return Array.from(powermeterDataMap.values())
  }

  return { handleFetchingPowermeterHistoryData, isFetching }
}
